import { useNavigate, useParams, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { Confirmation, Success, Error } from "../../Components/Modal";
import VehicleDetail from "./VehicleDetail";
import DriverDetail from "./DriverDetail";
import { getRideDetails, adminCancelRide, adminReassignRide, getInvoiceLink } from "../../Services/Ride";
import { getReviewByBookingId } from "../../Services/Review";
import { getVehicleTypeDetails } from "../../Services/VehicleType";
import { getUserDetails } from "../../Services/User";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import Map from "../../Components/Map";
import { format_date, resolveImageUrl } from "../../Utils/helpers";
import { getSetting } from "../../Services/Setting";
import BackButton from "../../Components/BackButton";
import ModalImageViewer from "../../Components/ModalImageViewer";
import { image_url, connection_string } from "../../Utils/connection_strings";
import "../Dashboard/Dashboard.css";
import { formatBookingCode } from "../../Utils/bookingCode";
import { adminSwalOptions } from "../../Utils/adminSwal";

const AVATAR_FALLBACK = "images/avatar.png";

const resolveVehicleTypeRef = (vehicleType) => {
  if (!vehicleType) return { id: null, name: null, data: null };
  if (typeof vehicleType === "object") {
    return {
      id: vehicleType._id || vehicleType.id || null,
      name: vehicleType.name || null,
      data: vehicleType,
    };
  }
  return { id: vehicleType, name: null, data: null };
};

const formatRideTimestamp = (value) => {
  if (!value) return null;
  const formatted = format_date(value, "MMM DD, YYYY, hh:mm A");
  return formatted === "Invalid date" ? null : formatted;
};

const isExpiredRide = (status) => status?.toLowerCase() === "expired";

const isCancelledRide = (status) => status?.toLowerCase() === "cancelled";

const isTerminalRide = (status) => isExpiredRide(status) || isCancelledRide(status);

const isCompletedRide = (status) => status?.toLowerCase() === "completed";

const isPendingRide = (status) => {
  const s = status?.toLowerCase();
  return s === "pending" || !s;
};

const isActiveRide = (status) => {
  const s = status?.toLowerCase();
  return ["accepted", "arrived", "started", "ongoing"].includes(s);
};

const getRideFareLabels = (status) => {
  if (isCompletedRide(status)) {
    return {
      cost: "Total Cost",
      summaryTitle: "Order Summary",
      total: "Total Paid by User",
      summaryNote: null,
    };
  }
  if (isExpiredRide(status)) {
    return {
      cost: "Quoted Fare",
      summaryTitle: "Quoted Fare Breakdown",
      total: "Quoted Total",
      summaryNote:
        "Estimated fare at booking. Ride expired before a driver accepted — payment was not captured.",
    };
  }
  if (isCancelledRide(status)) {
    return {
      cost: "Quoted Fare",
      summaryTitle: "Quoted Fare Breakdown",
      total: "Quoted Total",
      summaryNote:
        "Estimated fare at booking. Ride was cancelled — any payment hold was voided by the system.",
    };
  }
  if (isTerminalRide(status)) {
    return {
      cost: "Quoted Fare",
      summaryTitle: "Quoted Fare Breakdown",
      total: "Quoted Total",
      summaryNote: null,
    };
  }
  return {
    cost: "Estimated Fare",
    summaryTitle: "Estimated Fare Breakdown",
    total: "Estimated Total",
    summaryNote: isPendingRide(status)
      ? "Awaiting a driver to accept. Fare below is confirmed at booking — earnings apply only after completion."
      : "Ride is active. Fare below reflects the booked amount — earnings apply only after completion.",
  };
};

const getCancelledByLabel = (cancelledBy) => {
  const labels = {
    user: "User",
    driver: "Driver",
    admin: "Admin",
    system: "System (app closed during search)",
  };
  return labels[cancelledBy] || "Unknown";
};

const getPaymentStatusLabel = (ride) => {
  const status = ride?.status?.toLowerCase();
  const isPaid = Boolean(ride?.is_paid);

  if (status === "expired") {
    return isPaid
      ? "Hold released — not charged"
      : "Not charged";
  }

  if (status === "cancelled") {
    if (!isPaid) return "Not charged";
    if (ride?.cancelledBy === "driver") return "Hold released — full void";
    if (ride?.cancelledBy === "user") return "Hold released — voided";
    if (ride?.cancelledBy === "admin") return "Hold released — voided";
    return "Hold released — voided";
  }

  if (status === "completed") {
    return isPaid ? "Captured" : "Not captured";
  }

  if (isPendingRide(status)) {
    return isPaid ? "Authorized (hold placed)" : "Pending payment";
  }

  if (isActiveRide(status)) {
    return isPaid ? "Authorized (hold placed)" : "Payment pending";
  }

  return isPaid ? "Paid" : "Pending";
};

const getExpiredReason = (ride) =>
  ride?.cancellationReason?.trim() ||
  "No driver accepted within the search window.";

const getPickupTimeLabel = (ride) => {
  if (isTerminalRide(ride?.status)) {
    const actual =
      ride?.arrivedAt ||
      ride?.arriveTime ||
      ride?.startedAt ||
      ride?.startTime;
    if (actual) return formatRideTimestamp(actual);
    return "Not started";
  }

  if (isPendingRide(ride?.status)) {
    return "Awaiting driver";
  }

  const booking = ride?.bookingDetails;
  if (booking?.pickupTime) return booking.pickupTime;
  if (booking?.pickupDate) return formatRideTimestamp(booking.pickupDate);

  const actual =
    ride?.arrivedAt ||
    ride?.arriveTime ||
    ride?.startedAt ||
    ride?.startTime;
  if (actual) return formatRideTimestamp(actual);

  if (isActiveRide(ride?.status)) {
    return ride?.acceptedAt
      ? `Accepted ${formatRideTimestamp(ride.acceptedAt)}`
      : "Driver assigned";
  }

  return "ASAP";
};

const getDropOffTimeLabel = (ride) => {
  if (isTerminalRide(ride?.status)) {
    if (ride?.completedAt) return formatRideTimestamp(ride.completedAt);
    return "Not completed";
  }

  if (isPendingRide(ride?.status)) {
    return "After pickup";
  }

  const booking = ride?.bookingDetails;
  if (booking?.dropOffTime) return booking.dropOffTime;
  if (booking?.dropOffDate) return formatRideTimestamp(booking.dropOffDate);

  if (ride?.completedAt) return formatRideTimestamp(ride.completedAt);
  if (isActiveRide(ride?.status) || ride?.startedAt || ride?.startTime || ride?.arrivedAt || ride?.arriveTime) {
    return "In progress";
  }

  return "-";
};

const formatMoney = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return `$${amount.toFixed(2)}`;
};

const buildOrderSummary = (booking, { showEarnings = true } = {}) => {
  if (!booking) {
    return { lines: [], subtotal: null, total: null, earnings: null };
  }

  const lines = [];
  let subtotal = 0;

  const addLine = (label, rawValue) => {
    const amount = Number(rawValue);
    if (!Number.isFinite(amount) || amount <= 0) return;
    lines.push({ label, value: `$${amount.toFixed(2)}` });
    subtotal += amount;
  };

  addLine("Base Fare", booking.baseFare);

  const distanceAmount = booking.kmCost || booking.additionalMileCharge;
  if (distanceAmount) {
    const distanceLabel = booking.distance
      ? `Distance (${Number(booking.distance).toFixed(1)} mi)`
      : "Distance Fare";
    addLine(distanceLabel, distanceAmount);
  }

  addLine("Weight Surcharge", booking.weightFee);
  addLine("Helper Fee", booking.helperCost);
  addLine("Fragile Item Fee", booking.fragileCost);

  const totalPaid = Number(booking.price);
  const hasValidTotal = Number.isFinite(totalPaid) && totalPaid > 0;
  const discountAmount = Number(booking.discountAmount || 0);
  const promoCode = booking.promoCode;

  if (hasValidTotal && subtotal > 0) {
    const remainder = totalPaid + discountAmount - subtotal;
    if (remainder > 0.009) {
      addLine("Insurance / Other", remainder);
    }
  }

  if (discountAmount > 0) {
    const discountLabel = promoCode
      ? `Promo Discount (${promoCode})`
      : booking.appliedDiscountType === 'first_delivery'
        ? 'First Delivery Discount'
        : booking.appliedDiscountType === 'review'
          ? 'Review Reward Discount'
          : 'Discount';
    lines.push({ label: discountLabel, value: `-$${discountAmount.toFixed(2)}`, isDiscount: true });
  }

  const netFare = hasValidTotal ? totalPaid : Math.max(subtotal - discountAmount, 0);
  // Commission is charged on the fare before the customer's discount; the platform absorbs the discount.
  const grossFare = Number(booking.grossFare) > 0 ? Number(booking.grossFare) : netFare + discountAmount;
  const commission = Number(booking.adminCommission);

  let platformFee = null;
  let driverEarning = null;
  let platformLabel = "Platform Fee";

  if (showEarnings) {
    if (booking.adminCommissionAmt !== undefined && booking.driverTripFare !== undefined) {
      platformFee = Number(booking.adminCommissionAmt);
      driverEarning = Number(booking.driverTripFare);
      platformLabel = Number.isFinite(commission) && commission > 0 ? `Platform Fee (${commission}%)` : `Platform Fee`;
    } else if (Number.isFinite(commission) && commission > 0 && grossFare > 0) {
      platformFee = (grossFare * commission) / 100;
      driverEarning = grossFare - platformFee;
      platformLabel = `Platform Fee (${commission}%)`;
    }
  }

  return {
    lines,
    subtotal: subtotal > 0 ? `$${subtotal.toFixed(2)}` : null,
    total: hasValidTotal
      ? `$${totalPaid.toFixed(2)}`
      : subtotal > 0
        ? `$${subtotal.toFixed(2)}`
        : null,
    earnings:
      showEarnings && platformFee != null && driverEarning != null
        ? {
            grossFare: formatMoney(grossFare),
            discountAbsorbed: discountAmount > 0 ? formatMoney(discountAmount) : null,
            platformFee: formatMoney(platformFee),
            platformLabel: platformLabel,
            driverEarning: formatMoney(driverEarning),
          }
        : null,
  };
};

export default function RideDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [rideDetail, setRideDetail] = useState({});
  const [showViewer, setShowViewer] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [viewerImages, setViewerImages] = useState([]);
  const [viewerTitle, setViewerTitle] = useState("Image Viewer");
  const [driverProfileImageSrc, setDriverProfileImageSrc] = useState(AVATAR_FALLBACK);

  const { data, isLoading, refetch } = useQuery(["ride-details", id], () =>
    getRideDetails(id)
  );
  const { data: settings } = useQuery("get-setting", getSetting);

  const driverId = rideDetail?.driver?._id;

  const { data: driverDataResponse } = useQuery(
    ["ride-driver-details", driverId],
    () => getUserDetails(driverId),
    { enabled: Boolean(driverId) }
  );

  const fetchedDriver = driverDataResponse?.data?.data?.docs?.[0];
  const driverInfo = fetchedDriver || rideDetail?.driver;

  useEffect(() => {
    if (data?.data?.data?.docs?.length > 0)
      setRideDetail(data?.data?.data?.docs?.[0]);
  }, [data?.data?.data?.docs]);

  useEffect(() => {
    const profileSource =
      fetchedDriver?.profileImage ||
      (typeof rideDetail?.driver?.profileImage === "string"
        ? rideDetail.driver.profileImage
        : rideDetail?.driver?.profileImage);

    setDriverProfileImageSrc(resolveImageUrl(profileSource, AVATAR_FALLBACK));
  }, [fetchedDriver?.profileImage, rideDetail?.driver?.profileImage]);

  const {
    data: reviewData,
  } = useQuery(["booking-review", id], () => getReviewByBookingId(id), {
    enabled: !!id
  });

  const vehicleTypeRef = useMemo(
    () => resolveVehicleTypeRef(rideDetail?.bookingDetails?.vehicleType),
    [rideDetail?.bookingDetails?.vehicleType]
  );

  const { data: vehicleTypeResponse } = useQuery(
    ["ride-vehicle-type", vehicleTypeRef.id],
    () => getVehicleTypeDetails(vehicleTypeRef.id),
    { enabled: Boolean(vehicleTypeRef.id && !vehicleTypeRef.name) }
  );

  const requestedVehicleType = useMemo(() => {
    if (vehicleTypeRef.name) return vehicleTypeRef.data;
    const fetched = vehicleTypeResponse?.data?.data?.docs?.[0];
    return fetched || vehicleTypeRef.data;
  }, [vehicleTypeRef, vehicleTypeResponse?.data?.data?.docs]);

  const requestedVehicleName = requestedVehicleType?.name || null;
  const requestedVehicleWeight =
    requestedVehicleType?.weight || requestedVehicleType?.weightLimit || null;
  const requestedVehicleHelpers = Number(requestedVehicleType?.helperCount || 0);

  // Safe image mapping helper
  const mapImageObjectOrString = (img) => {
    let urlPath = "";
    if (typeof img === 'string') {
      urlPath = img;
    } else if (img && typeof img === 'object') {
      urlPath = img.url || img.path || img.imageUrl || '';
    }
    
    if (!urlPath) return '';
    if (urlPath.startsWith('http://') || urlPath.startsWith('https://')) {
      return urlPath;
    }
    // Clean up "public/" prefix since express.static mounts the public folder at root "/"
    let cleanPath = urlPath;
    if (cleanPath.startsWith('public/')) {
      cleanPath = cleanPath.slice(7);
    } else if (cleanPath.startsWith('/public/')) {
      cleanPath = cleanPath.slice(8);
    }
    
    // Ensure it doesn't double slash
    const base = image_url.endsWith('/') ? image_url : `${image_url}/`;
    const path = cleanPath.startsWith('/') ? cleanPath.slice(1) : cleanPath;
    return `${base}${path}`;
  };

  let rawPackageImages = rideDetail?.bookingDetails?.package?.images;
  if (rawPackageImages && !Array.isArray(rawPackageImages)) {
    rawPackageImages = [rawPackageImages];
  }
  const packageImages = rawPackageImages?.map(mapImageObjectOrString).filter(Boolean) || [];

  let rawSafetyImages = rideDetail?.safetyImages;
  if (rawSafetyImages && !Array.isArray(rawSafetyImages)) {
    rawSafetyImages = [rawSafetyImages];
  }
  const safetyImages = rawSafetyImages?.map(mapImageObjectOrString).filter(Boolean) || [];

  let rawPickupImages = rideDetail?.pickupImages;
  if (rawPickupImages && !Array.isArray(rawPickupImages)) {
    rawPickupImages = [rawPickupImages];
  }
  const pickupImages = rawPickupImages?.map(mapImageObjectOrString).filter(Boolean) || [];

  const pickupSignatureUrl = rideDetail?.pickupSignature ? mapImageObjectOrString(rideDetail.pickupSignature) : '';

  let rawDeliveredImages = rideDetail?.deliveredImages;
  if (rawDeliveredImages && !Array.isArray(rawDeliveredImages)) {
    rawDeliveredImages = [rawDeliveredImages];
  }
  const deliveredImages = rawDeliveredImages?.map(mapImageObjectOrString).filter(Boolean) || [];

  const signatureUrl = rideDetail?.signature ? mapImageObjectOrString(rideDetail.signature) : '';

  const handleDriverImageError = () => {
    setDriverProfileImageSrc(AVATAR_FALLBACK);
  };

  const pickupTimeLabel = useMemo(() => getPickupTimeLabel(rideDetail), [rideDetail]);
  const dropOffTimeLabel = useMemo(() => getDropOffTimeLabel(rideDetail), [rideDetail]);

  const isCancelled = isCancelledRide(rideDetail?.status);
  const isExpired = isExpiredRide(rideDetail?.status);
  const isCompleted = isCompletedRide(rideDetail?.status);
  const isPending = isPendingRide(rideDetail?.status);
  const isActive = isActiveRide(rideDetail?.status);
  const hadDriverAssigned = Boolean(rideDetail?.driver?._id && rideDetail?.driver?.firstName);
  const fareLabels = getRideFareLabels(rideDetail?.status);

  const orderSummary = useMemo(
    () => buildOrderSummary(rideDetail?.bookingDetails, { showEarnings: isCompleted }),
    [rideDetail?.bookingDetails, isCompleted]
  );

  const paymentStatusLabel = useMemo(
    () => getPaymentStatusLabel(rideDetail),
    [rideDetail]
  );

  const handleImageClick = (images, index, title) => {
    setViewerImages(images);
    setViewerIndex(index);
    setViewerTitle(title);
    setShowViewer(true);
  };

  const rideStatus = rideDetail?.status?.toLowerCase();
  const canAdminCancel = ["pending", "accepted", "arrived", "started", "ongoing"].includes(rideStatus);
  const canReassign = ["accepted", "arrived"].includes(rideStatus);
  const [adminActionBusy, setAdminActionBusy] = useState(false);
  const captureStatus = rideDetail?.paymentCaptureStatus;
  const hasCustomerInvoice =
    (rideStatus === "completed" && captureStatus !== "failed") ||
    (rideStatus === "cancelled" && captureStatus === "partial_fee" && Number(rideDetail?.cancellationFee) > 0);
  const hasMoverStatement = rideStatus === "completed" && Boolean(rideDetail?.driver?._id);

  const openInvoice = async (type) => {
    // Opened before the request so the browser does not treat it as an unwanted popup.
    const tab = window.open("", "_blank");
    try {
      const res = await getInvoiceLink(id, type);
      const url = `${connection_string}${res?.data?.data?.path}`;
      if (tab) tab.location.href = url;
      else window.location.assign(url);
    } catch (err) {
      tab?.close();
      Error(err?.response?.data?.message || "Could not prepare the document.");
    }
  };

  const handleAdminRideAction = async (type) => {
    const isCancel = type === "cancel";
    const result = await Swal.fire({
      ...adminSwalOptions,
      title: isCancel ? "Cancel this ride?" : "Reassign this ride to a new driver?",
      html: `<p class="admin-swal-message">${isCancel
        ? "The user and driver are notified and the user's payment hold is released."
        : "The current driver is removed and the search for a new driver restarts. The user is notified."}</p>`,
      input: "textarea",
      inputLabel: "Reason (shared with user and driver)",
      inputValidator: (value) => (!value?.trim() ? "Please enter a reason" : undefined),
      showCancelButton: true,
      confirmButtonText: isCancel ? "Cancel Ride" : "Reassign",
      cancelButtonText: "Back",
      icon: "warning",
    });
    if (!result.isConfirmed) return;

    setAdminActionBusy(true);
    try {
      const reason = result.value.trim();
      const res = isCancel ? await adminCancelRide(id, reason) : await adminReassignRide(id, reason);
      Success(isCancel ? "Ride Cancelled" : "Ride Reassigned", res?.data?.message);
      refetch();
    } catch (err) {
      Error(err?.response?.data?.message);
      refetch();
    } finally {
      setAdminActionBusy(false);
    }
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    if (s === 'completed') return '#28a745';
    if (s === 'cancelled' || s === 'expired') return '#dc3545';
    if (s === 'started' || s === 'ongoing' || s === 'arrived' || s === 'accepted') return '#fb9809';
    return '#6c757d';
  };

  return (
    <>
      <div className="app-content content dashboard">
        <div className="content-wrapper">
          <div className="content-body">
            <section id="configuration">
              <div className="user-details-wrapper">
                {/* Header Section */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="back-title d-flex flex-wrap align-items-center justify-content-between gap-3">
                      <div className="d-flex align-items-center">
                        <BackButton />
                        <h1 className="pageTitle m-0 ms-3 fw-800 fs-2">Ride Details</h1>
                      </div>
                      <div className="ride-header-actions d-flex flex-wrap align-items-center gap-3">
                        <div className="ride-header-meta d-flex align-items-center gap-3">
                          <span className="badge rounded-pill px-4 py-2 text-uppercase fw-bold" style={{ backgroundColor: getStatusColor(rideDetail?.status), fontSize: '0.85rem' }}>
                            {rideDetail?.status || 'Pending'}
                          </span>
                          {rideDetail?._id && <span className="text-muted small fw-semibold">{formatBookingCode(rideDetail)}</span>}
                        </div>
                        {hasCustomerInvoice && (
                          <button
                            type="button"
                            className="siteBtn header-btn-standard"
                            onClick={() => openInvoice("customer")}
                          >
                            <i className="fas fa-file-invoice me-2"></i>
                            {rideStatus === "cancelled" ? "Cancellation Invoice" : "User Statement"}
                          </button>
                        )}
                        {hasMoverStatement && (
                          <button
                            type="button"
                            className="siteBtn header-btn-standard"
                            onClick={() => openInvoice("driver")}
                          >
                            <i className="fas fa-file-invoice-dollar me-2"></i>Driver Statement
                          </button>
                        )}
                        {canReassign && (
                          <button
                            type="button"
                            className="siteBtn header-btn-standard"
                            disabled={adminActionBusy}
                            onClick={() => handleAdminRideAction("reassign")}
                          >
                            <i className="fas fa-random me-2"></i>Reassign Driver
                          </button>
                        )}
                        {canAdminCancel && (
                          <button
                            type="button"
                            className="siteBtn header-btn-standard"
                            style={{ backgroundColor: "#dc3545", borderColor: "#dc3545" }}
                            disabled={adminActionBusy}
                            onClick={() => handleAdminRideAction("cancel")}
                          >
                            <i className="fas fa-ban me-2"></i>Cancel Ride
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-xl-12">
                    {/* Map Section */}
                    <div className="details-card-premium mb-4 p-0 overflow-hidden ride-map-card">
                      <div style={{ height: '400px', width: '100%' }}>
                        {rideDetail?._id ? (
                          <Map
                            originLat={rideDetail?.bookingDetails?.pickupLocation?.lat}
                            originLng={rideDetail?.bookingDetails?.pickupLocation?.lng}
                            destinationLat={rideDetail?.bookingDetails?.dropOffLocation?.lat}
                            destinationLng={rideDetail?.bookingDetails?.dropOffLocation?.lng}
                          />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center h-100 bg-light">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading data...</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="row">
                      {/* Main Details Card */}
                      <div className={hadDriverAssigned ? "col-lg-8" : "col-lg-12"}>
                        <div className="details-card-premium ride-details-card mb-4">
                          <div className="ride-details-card__header">
                            <h4 className="ride-details-card__title mb-0">Booking Information</h4>
                          </div>

                          <div className="ride-booking-summary">
                            <div className="ride-booking-summary__item">
                              <span className="ride-booking-summary__label">User</span>
                              <span className="ride-booking-summary__value">
                                {rideDetail?.user?.firstName} {rideDetail?.user?.lastName}
                              </span>
                            </div>
                            <div className="ride-booking-summary__item">
                              <span className="ride-booking-summary__label">Booking Date</span>
                              <span className="ride-booking-summary__value">
                                {rideDetail?.createdAt
                                  ? format_date(rideDetail.createdAt, "MMM DD, YYYY, hh:mm A")
                                  : "-"}
                              </span>
                            </div>
                            <div className="ride-booking-summary__item ride-booking-summary__item--cost">
                              <span className="ride-booking-summary__label">
                                {fareLabels.cost}
                              </span>
                              <span className="ride-booking-summary__cost">
                                {rideDetail?.bookingDetails?.price
                                  ? `$${rideDetail.bookingDetails.price.toFixed(2)}`
                                  : "-"}
                              </span>
                            </div>
                          </div>

                          {isExpired && (
                            <div className="ride-expired-card">
                              <div className="ride-expired-card__header">
                                <div className="ride-expired-card__icon">
                                  <i className="fas fa-hourglass-end" />
                                </div>
                                <div>
                                  <h5 className="ride-expired-card__title mb-1">Ride Expired</h5>
                                  <p className="ride-expired-card__subtitle mb-0">
                                    No driver accepted in time. The booking closed automatically and no earnings were processed.
                                  </p>
                                </div>
                              </div>
                              <div className="ride-expired-card__grid">
                                <div className="ride-expired-card__item">
                                  <span className="ride-expired-card__label">Closed By</span>
                                  <span className="ride-expired-card__value">System (search timeout)</span>
                                </div>
                                <div className="ride-expired-card__item">
                                  <span className="ride-expired-card__label">Expired On</span>
                                  <span className="ride-expired-card__value">
                                    {rideDetail?.updatedAt
                                      ? format_date(rideDetail.updatedAt, "MMM DD, YYYY, hh:mm A")
                                      : "-"}
                                  </span>
                                </div>
                                <div className="ride-expired-card__item">
                                  <span className="ride-expired-card__label">Payment Status</span>
                                  <span className="ride-expired-card__value">{paymentStatusLabel}</span>
                                </div>
                                <div className="ride-expired-card__item">
                                  <span className="ride-expired-card__label">Driver Assigned</span>
                                  <span className="ride-expired-card__value">None</span>
                                </div>
                              </div>
                              <div className="ride-expired-card__reason">
                                <span className="ride-expired-card__label">What happened</span>
                                <p className="ride-expired-card__reason-text mb-0">
                                  {getExpiredReason(rideDetail)}
                                </p>
                                {rideDetail?.is_paid && (
                                  <p className="ride-expired-card__payment-note mb-0">
                                    A payment hold was placed at booking and automatically released when the ride expired — the user was not charged.
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          {isCancelled && (
                            <div className="ride-cancellation-card">
                              <div className="ride-cancellation-card__header">
                                <div className="ride-cancellation-card__icon">
                                  <i className="fas fa-ban" />
                                </div>
                                <div>
                                  <h5 className="ride-cancellation-card__title mb-1">Ride Cancelled</h5>
                                  <p className="ride-cancellation-card__subtitle mb-0">
                                    This booking was cancelled before completion. No driver earnings were processed.
                                  </p>
                                </div>
                              </div>
                              <div className="ride-cancellation-card__grid">
                                <div className="ride-cancellation-card__item">
                                  <span className="ride-cancellation-card__label">Cancelled By</span>
                                  <span className="ride-cancellation-card__value">
                                    {getCancelledByLabel(rideDetail?.cancelledBy)}
                                  </span>
                                </div>
                                <div className="ride-cancellation-card__item">
                                  <span className="ride-cancellation-card__label">Cancelled On</span>
                                  <span className="ride-cancellation-card__value">
                                    {rideDetail?.updatedAt
                                      ? format_date(rideDetail.updatedAt, "MMM DD, YYYY, hh:mm A")
                                      : "-"}
                                  </span>
                                </div>
                                <div className="ride-cancellation-card__item">
                                  <span className="ride-cancellation-card__label">Payment Status</span>
                                  <span className="ride-cancellation-card__value">{paymentStatusLabel}</span>
                                </div>
                                {hadDriverAssigned && (
                                  <div className="ride-cancellation-card__item">
                                    <span className="ride-cancellation-card__label">Driver Assigned</span>
                                    <span className="ride-cancellation-card__value">
                                      {driverInfo?.firstName} {driverInfo?.lastName}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="ride-cancellation-card__reason">
                                <span className="ride-cancellation-card__label">Reason</span>
                                <p className="ride-cancellation-card__reason-text mb-0">
                                  {rideDetail?.cancellationReason || "No specific reason provided."}
                                </p>
                                {rideDetail?.is_paid && (
                                  <p className="ride-cancellation-card__payment-note mb-0">
                                    Payment hold was voided on cancellation — the fare was not captured unless a cancellation fee applies.
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          {Array.isArray(rideDetail?.reassignLog) && rideDetail.reassignLog.length > 0 && (
                            <div className="alert alert-warning mb-3" role="alert">
                              <strong>Driver changes ({rideDetail.reassignLog.length})</strong>
                              <ul className="mb-0 mt-1 ps-3">
                                {rideDetail.reassignLog.map((entry, idx) => (
                                  <li key={`${entry?.at}-${idx}`}>
                                    {entry?.by === "driver" ? "Driver cancelled" : "Reassigned by admin"}
                                    {entry?.at ? ` on ${format_date(entry.at, "MMM DD, YYYY, hh:mm A")}` : ""}
                                    {entry?.reason ? ` - ${entry.reason}` : ""}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {isPending && (
                            <div className="ride-pending-card">
                              <div className="ride-pending-card__header">
                                <div className="ride-pending-card__icon">
                                  <i className="fas fa-clock" />
                                </div>
                                <div>
                                  <h5 className="ride-pending-card__title mb-1">Awaiting Driver Assignment</h5>
                                  <p className="ride-pending-card__subtitle mb-0">
                                    Searching for an available driver near the pickup location.
                                  </p>
                                </div>
                              </div>
                              <div className="ride-pending-card__grid">
                                <div className="ride-pending-card__item">
                                  <span className="ride-pending-card__label">Ride Status</span>
                                  <span className="ride-pending-card__value">Pending</span>
                                </div>
                                <div className="ride-pending-card__item">
                                  <span className="ride-pending-card__label">Payment Status</span>
                                  <span className="ride-pending-card__value">{paymentStatusLabel}</span>
                                </div>
                                <div className="ride-pending-card__item">
                                  <span className="ride-pending-card__label">Driver Assigned</span>
                                  <span className="ride-pending-card__value">Not yet</span>
                                </div>
                                <div className="ride-pending-card__item">
                                  <span className="ride-pending-card__label">Pickup</span>
                                  <span className="ride-pending-card__value">Awaiting driver</span>
                                </div>
                              </div>
                            </div>
                          )}

                          {isActive && (
                            <div className="ride-active-card">
                              <div className="ride-active-card__header">
                                <div className="ride-active-card__icon">
                                  <i className="fas fa-shipping-fast" />
                                </div>
                                <div>
                                  <h5 className="ride-active-card__title mb-1">Ride In Progress</h5>
                                  <p className="ride-active-card__subtitle mb-0">
                                    {hadDriverAssigned
                                      ? "A driver has accepted this booking."
                                      : "Ride is active — awaiting full assignment details."}
                                  </p>
                                </div>
                              </div>
                              <div className="ride-active-card__grid">
                                <div className="ride-active-card__item">
                                  <span className="ride-active-card__label">Current Status</span>
                                  <span className="ride-active-card__value text-capitalize">
                                    {rideDetail?.status || "-"}
                                  </span>
                                </div>
                                <div className="ride-active-card__item">
                                  <span className="ride-active-card__label">Payment Status</span>
                                  <span className="ride-active-card__value">{paymentStatusLabel}</span>
                                </div>
                                {rideDetail?.acceptedAt && (
                                  <div className="ride-active-card__item">
                                    <span className="ride-active-card__label">Accepted On</span>
                                    <span className="ride-active-card__value">
                                      {format_date(rideDetail.acceptedAt, "MMM DD, YYYY, hh:mm A")}
                                    </span>
                                  </div>
                                )}
                                {hadDriverAssigned && (
                                  <div className="ride-active-card__item">
                                    <span className="ride-active-card__label">Driver</span>
                                    <span className="ride-active-card__value">
                                      {driverInfo?.firstName} {driverInfo?.lastName}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {(requestedVehicleName || vehicleTypeRef.id) && (
                            <div className="ride-requested-vehicle">
                              <div className="ride-requested-vehicle__icon">
                                <i className="fas fa-truck" />
                              </div>
                              <div className="ride-requested-vehicle__content">
                                <span className="ride-requested-vehicle__label">Requested Vehicle</span>
                                <span className="ride-requested-vehicle__name">
                                  {requestedVehicleName || "Loading..."}
                                </span>
                                <div className="ride-requested-vehicle__meta">
                                  {requestedVehicleWeight && (
                                    <span>Up to {requestedVehicleWeight} lbs</span>
                                  )}
                                  {requestedVehicleHelpers > 0 && (
                                    <span>{requestedVehicleHelpers} Helper{requestedVehicleHelpers > 1 ? "s" : ""}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="ride-location-card">
                            <div className="ride-location-card__row">
                              <div className="ride-location-card__icon ride-location-card__icon--pickup">
                                <i className="fas fa-map-marker-alt" />
                              </div>
                              <div>
                                <span className="ride-location-card__label">Pickup Location</span>
                                <p className="ride-location-card__address mb-1">
                                  {rideDetail?.bookingDetails?.pickupLocation?.name || "-"}
                                </p>
                                <span className="ride-location-card__time">
                                  Pickup Time: {pickupTimeLabel}
                                </span>
                              </div>
                            </div>
                            <div className="ride-location-card__divider" />
                            <div className="ride-location-card__row">
                              <div className="ride-location-card__icon ride-location-card__icon--dropoff">
                                <i className="fas fa-flag-checkered" />
                              </div>
                              <div>
                                <span className="ride-location-card__label">Drop-off Location</span>
                                <p className="ride-location-card__address mb-1">
                                  {rideDetail?.bookingDetails?.dropOffLocation?.name || "-"}
                                </p>
                                <span className="ride-location-card__time">
                                  Drop-off Time: {dropOffTimeLabel}
                                </span>
                              </div>
                            </div>
                          </div>

                          {(orderSummary.lines.length > 0 || orderSummary.total) && (
                            <div className="ride-order-summary">
                              <div className="ride-order-summary__header">
                                <div className="ride-order-summary__header-icon">
                                  <i className="fas fa-receipt" />
                                </div>
                                <h5 className="ride-order-summary__title mb-0">
                                  {fareLabels.summaryTitle}
                                </h5>
                              </div>
                              {fareLabels.summaryNote && (
                                <p className="ride-order-summary__cancelled-note mb-0">
                                  {fareLabels.summaryNote}
                                </p>
                              )}
              <div className="ride-order-summary__lines">
                {orderSummary.lines.map((line) => (
                  <div
                    key={line.label}
                    className={`ride-order-summary__line${line.isDiscount ? " ride-order-summary__line--discount" : ""}`}
                  >
                    <span>{line.label}</span>
                    <span>{line.value}</span>
                  </div>
                ))}
              </div>
                              {orderSummary.subtotal && orderSummary.lines.length > 1 && (
                                <div className="ride-order-summary__subtotal">
                                  <span>Subtotal</span>
                                  <span>{orderSummary.subtotal}</span>
                                </div>
                              )}
                              {orderSummary.total && (
                                <div className="ride-order-summary__total">
                                  <span>{fareLabels.total}</span>
                                  <span>{orderSummary.total}</span>
                                </div>
                              )}

                              {orderSummary.earnings && (
                                <div className="ride-order-summary__earnings">
                                  <div className="ride-order-summary__earnings-title">
                                    <i className="fas fa-chart-pie" />
                                    Earnings Split
                                  </div>
                                  <p className="ride-order-summary__earnings-note mb-0">
                                    Platform fee is deducted from the fare above — not added on top.
                                    {orderSummary.earnings.discountAbsorbed &&
                                      ` The user discount (${orderSummary.earnings.discountAbsorbed}) is covered by Muvrrs and does not reduce the driver's earning.`}
                                  </p>
                                  <div className="ride-order-summary__lines">
                                    <div className="ride-order-summary__line">
                                      <span>Gross Fare (before discount)</span>
                                      <span>{orderSummary.earnings.grossFare}</span>
                                    </div>
                                    <div className="ride-order-summary__line ride-order-summary__line--deduction">
                                      <span>{orderSummary.earnings.platformLabel}</span>
                                      <span>{orderSummary.earnings.platformFee}</span>
                                    </div>
                                    <div className="ride-order-summary__line ride-order-summary__line--highlight">
                                      <span>Driver Earning</span>
                                      <span>{orderSummary.earnings.driverEarning}</span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="ride-package-card">
                            <div className="ride-package-card__header">
                              <div className="ride-package-card__header-icon">
                                <i className="fas fa-box-open" />
                              </div>
                              <h5 className="ride-package-card__title mb-0">Package Details</h5>
                            </div>

                            <div className="ride-package-card__stats">
                              <div className="ride-package-card__stat">
                                <span className="ride-package-card__label">Items</span>
                                <span className="ride-package-card__value">
                                  {rideDetail?.bookingDetails?.package?.item?.join(", ") || "General Items"}
                                </span>
                              </div>
                              <div className="ride-package-card__stat">
                                <span className="ride-package-card__label">Estimated Weight</span>
                                <span className="ride-package-card__value">
                                  {rideDetail?.bookingDetails?.package?.weight
                                    ? `${rideDetail.bookingDetails.package.weight} lbs`
                                    : "-"}
                                </span>
                              </div>
                            </div>

                            {packageImages.length > 0 && (
                              <>
                                <div className="ride-package-card__divider" />
                                <div className="ride-package-card__images">
                                  <span className="ride-package-card__label">Package Images</span>
                                  <div className="ride-package-card__images-row">
                                    {packageImages.map((img, index) => (
                                      <button
                                        key={img + index}
                                        type="button"
                                        onClick={() => handleImageClick(packageImages, index, "Package Images")}
                                        className="ride-image-thumb"
                                      >
                                        <img src={img} alt={`Package ${index + 1}`} />
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </>
                            )}
                          </div>

                          {/* Safety Proof Images */}
                          {safetyImages.length > 0 && (
                            <div className="safety-proof-details mt-4 pt-4 border-top">
                              <h5 className="fw-bold text-dark mb-4">Safety Proof Images (By User)</h5>
                              <div className="row">
                                <div className="col-12">
                                  <div className="d-flex flex-wrap gap-3">
                                    {safetyImages.map((img, index) => (
                                      <div key={index} onClick={() => handleImageClick(safetyImages, index, "Safety Proof Images")} className="cursor-pointer image-hover">
                                        <img src={img} alt="safety proof" className="rounded-3 border shadow-sm" style={{ width: '100px', height: '100px', objectFit: 'cover' }} />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Pickup Details / Pickup Proof */}
                          {(pickupImages.length > 0 || pickupSignatureUrl) && (
                            <div className="pickup-details mt-4 pt-4 border-top">
                              <h5 className="fw-bold text-dark mb-4">Pickup Proof (Before Delivery)</h5>
                              
                              <div className="row">
                                {pickupImages.length > 0 && (
                                  <div className="col-md-6 mb-4 mb-md-0">
                                    <label className="text-secondary small fw-bold text-uppercase mb-3 d-block">Pickup Images</label>
                                    <div className="d-flex flex-wrap gap-3">
                                      {pickupImages.map((img, index) => (
                                        <div key={index} onClick={() => handleImageClick(pickupImages, index, "Pickup Images")} className="cursor-pointer image-hover">
                                          <img src={img} alt="pickup" className="rounded-3 border shadow-sm" style={{ width: '100px', height: '100px', objectFit: 'cover' }} />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {pickupSignatureUrl && (
                                  <div className="col-md-6">
                                    <label className="text-secondary small fw-bold text-uppercase mb-3 d-block">Sender's Signature</label>
                                    <div className="signature-wrapper p-2 bg-light rounded-3 border d-inline-block">
                                      <img 
                                        src={pickupSignatureUrl} 
                                        alt="Sender's Signature" 
                                        className="bg-white rounded"
                                        style={{ maxWidth: '250px', maxHeight: '120px', objectFit: 'contain' }} 
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Delivered Details / Delivery Proof */}
                          {isCompleted && (
                            <div className="delivery-details mt-4 pt-4 border-top">
                              <h5 className="fw-bold text-dark mb-4">Delivery Proof</h5>
                              
                              <div className="row">
                                {deliveredImages.length > 0 && (
                                  <div className="col-md-6 mb-4 mb-md-0">
                                    <label className="text-secondary small fw-bold text-uppercase mb-3 d-block">Delivered Images</label>
                                    <div className="d-flex flex-wrap gap-3">
                                      {deliveredImages.map((img, index) => (
                                        <div key={index} onClick={() => handleImageClick(deliveredImages, index, "Delivered Images")} className="cursor-pointer image-hover">
                                          <img src={img} alt="delivered" className="rounded-3 border shadow-sm" style={{ width: '100px', height: '100px', objectFit: 'cover' }} />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {signatureUrl && (
                                  <div className="col-md-6">
                                    <label className="text-secondary small fw-bold text-uppercase mb-3 d-block">Receiver's Signature</label>
                                    <div className="signature-wrapper p-2 bg-light rounded-3 border d-inline-block">
                                      <img 
                                        src={signatureUrl} 
                                        alt="Receiver's Signature" 
                                        className="bg-white rounded"
                                        style={{ maxWidth: '250px', maxHeight: '120px', objectFit: 'contain' }} 
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {isCompleted && (
                            <div className="ride-reviews-section mt-4 pt-4 border-top">
                              <h5 className="fw-bold text-dark mb-4 d-flex align-items-center gap-2">
                                <i className="fas fa-star text-warning" /> Ride Reviews
                              </h5>

                              {reviewData?.data?.data?.customerReview ? (
                                <div className="ride-review-card bg-light p-3 rounded-3 mb-3">
                                  <div className="fw-bold text-dark mb-2">User → Driver</div>
                                  <div className="d-flex align-items-center gap-1 mb-2 flex-wrap">
                                    {[...Array(5)].map((_, i) => (
                                      <i
                                        key={i}
                                        className={`fas fa-star ${i < reviewData.data.data.customerReview.rating ? 'text-warning' : 'text-muted'}`}
                                      />
                                    ))}
                                    <span className="ms-2 fw-bold">{reviewData.data.data.customerReview.rating}/5</span>
                                  </div>
                                  {reviewData.data.data.customerReview.message ? (
                                    <p className="ride-review-comment text-muted small mb-0">
                                      {reviewData.data.data.customerReview.message}
                                    </p>
                                  ) : null}
                                </div>
                              ) : (
                                <p className="text-muted small mb-3">User has not reviewed this ride yet.</p>
                              )}

                              {reviewData?.data?.data?.driverReview ? (
                                <div className="ride-review-card bg-light p-3 rounded-3">
                                  <div className="fw-bold text-dark mb-2">Driver → User</div>
                                  <div className="d-flex align-items-center gap-1 mb-2 flex-wrap">
                                    {[...Array(5)].map((_, i) => (
                                      <i
                                        key={i}
                                        className={`fas fa-star ${i < reviewData.data.data.driverReview.rating ? 'text-warning' : 'text-muted'}`}
                                      />
                                    ))}
                                    <span className="ms-2 fw-bold">{reviewData.data.data.driverReview.rating}/5</span>
                                  </div>
                                  {reviewData.data.data.driverReview.message ? (
                                    <p className="ride-review-comment text-muted small mb-0">
                                      {reviewData.data.data.driverReview.message}
                                    </p>
                                  ) : null}
                                </div>
                              ) : (
                                <p className="text-muted small mb-0">Driver has not reviewed this user yet.</p>
                              )}
                            </div>
                          )}

                        </div>
                      </div>

                      {/* Side Cards: Driver & Vehicle */}
                      {hadDriverAssigned && (
                        <div className="col-lg-4">
                          <div className="details-card-premium p-4 bg-white rounded-4 shadow-sm border mb-4">
                            <h5 className="fw-bold text-dark mb-4 d-flex align-items-center gap-2">
                              <i className="fas fa-user-tie text-primary" /> Driver Info
                              {isCancelled && (
                                <span className="ride-side-badge ride-side-badge--muted">Was assigned</span>
                              )}
                            </h5>
                            <div className="text-center mb-4">
                              <img 
                                src={driverProfileImageSrc} 
                                alt="" 
                                className="rounded-circle border p-1 shadow-sm"
                                style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                onError={handleDriverImageError}
                              />
                              <h5 className="mt-3 mb-1 fw-bold">{driverInfo?.firstName} {driverInfo?.lastName}</h5>
                              <span className="text-muted small d-block mb-2">{driverInfo?.email}</span>
                              <div className="badge bg-primary-light text-primary rounded-pill px-3 py-1 fw-bold" style={{ fontSize: '0.75rem' }}>
                                <i className="fas fa-star me-1" /> {driverInfo?.averageRating || rideDetail?.driver?.averageRating || 'N/A'}
                              </div>
                            </div>
                            <div className="border-top pt-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="text-secondary small fw-bold">Phone:</span>
                                <span className="fw-semibold small">{driverInfo?.phone}</span>
                              </div>
                              <button 
                                className="btn btn-outline-primary btn-sm w-100 mt-2 rounded-pill fw-bold"
                                data-bs-toggle="modal" 
                                data-bs-target="#driverDetails"
                              >
                                View Full Profile
                              </button>
                            </div>
                          </div>

                          {rideDetail?.driver?.vehicle?._id && rideDetail?.driver?.vehicle?.name && (
                            <div className="details-card-premium p-4 bg-white rounded-4 shadow-sm border mb-4">
                              <h5 className="fw-bold text-dark mb-4 d-flex align-items-center gap-2">
                                <i className="fas fa-truck text-primary" /> Vehicle Info
                              </h5>
                              <div className="row align-items-center mb-3">
                                <div className="col-8">
                                  <h6 className="mb-1 fw-bold">{rideDetail?.driver?.vehicle?.name}</h6>
                                  <span className="badge bg-light text-dark border">{rideDetail?.driver?.vehicle?.type?.name}</span>
                                </div>
                              </div>
                              <div className="border-top pt-3">
                                <div className="d-flex justify-content-between mb-2">
                                  <span className="text-secondary small fw-bold">Plate No:</span>
                                  <span className="fw-bold text-dark">{rideDetail?.driver?.vehicle?.numberPlate}</span>
                                </div>
                                <button 
                                  className="btn btn-outline-primary btn-sm w-100 mt-2 rounded-pill fw-bold"
                                  data-bs-toggle="modal" 
                                  data-bs-target="#vehicleDetails"
                                >
                                  Vehicle Details
                                </button>
                              </div>
                            </div>
                          )}

                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Modals */}
      <DriverDetail data={driverInfo} />
      <VehicleDetail 
        data={rideDetail?.driver} 
        bookingDetails={rideDetail?.bookingDetails} 
      />
      <ModalImageViewer
        show={showViewer}
        images={viewerImages}
        startIndex={viewerIndex}
        onHide={() => setShowViewer(false)}
        title={viewerTitle}
      />

      <style>{`
        .fw-800 { font-weight: 800 !important; }
        .ride-review-comment,
        .ride-review-card {
          max-width: 100%;
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        .ride-review-card {
          border: 1px solid #ececf0;
        }
      `}</style>
    </>
  );
}
